import { Component, inject, NgModule } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';
import { MobileGuard } from '@core/guards/mobile.guard';
import { WebGuard } from '@core/guards/web.guard';
import { AppAccessGuard } from '@core/guards/app-access-guard';
import { OrganizationGuard } from '@core/guards/organization.guard';
import { AuthGuard } from '@osc-utils/authentication/auth-guard';
import { ApplicationAdminGuard } from '@osc-utils/authentication/application-admin-guard';
import { AuthFailureGuard, AuthLogoutGuard } from '@evc/platform';
import { CurrentUserService } from '@core/services/current-user.service';
import { UserRedirectComponent } from '@shared/components/user-redirect/user-redirect.component';
import { CustomPreloadingStrategyService } from './custom-preloading-strategy';
import { InspectionRequestDeficiencyViewComponent } from './deficiency/components';
import { LogoutComponent } from './mobile/components/logout/logout.component';
import { AccessDeniedComponent } from './access-denied/access-denied.component';
import { WrongOrganizationComponent } from './wrong-organization/wrong-organization.component';

@Component({ template: '', selector: 'app-empty-component' })
class EmptyComponent {}

const defaultRoute = '/';
const routes: Routes = [
  {
    path: '',
    redirectTo: '/inspection-requests',
    pathMatch: 'full',
  },
  {
    path: 'organizations/:orgId',
    canActivate: [AuthGuard, OrganizationGuard, AppAccessGuard],
    children: [
      {
        path: 'projects',
        canActivate: [WebGuard],
        loadChildren: () => import('./projects/projects.module').then(m => m.ProjectsModule),
      },
      {
        path: 'inspection-requests',
        canActivate: [WebGuard],
        data: { preload: true },
        loadChildren: () => import('./inspection-requests/inspection-requests.module').then(m => m.InspectionRequestsModule),
      },
      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then(m => m.SettingsModule),
      },
      {
        path: 'deficiency:inspectionRequestId',
        canActivate: [ApplicationAdminGuard],
        component: InspectionRequestDeficiencyViewComponent,
      },
    ],
  },
  {
    path: 'mobile',
    canActivate: [AuthGuard, MobileGuard],
    loadChildren: () => import('./mobile/mobile.module').then(m => m.MobileModule),
  },
  {
    path: 'user-redirect',
    component: UserRedirectComponent,
  },
  {
    path: 'access-denied',
    component: AccessDeniedComponent,
  },
  {
    path: 'wrong-organization',
    component: WrongOrganizationComponent,
  },
  {
    path: 'logout',
    canActivate: [AuthLogoutGuard],
    data: { logout: true }, // to prevent it from fetching any token
    component: EmptyComponent, // can be any component
  },
  {
    path: 'auth-fail',
    canActivate: [AuthFailureGuard],
    component: EmptyComponent, // can be any component
  },
  {
    path: 'mobile-auth', // This route is used to redirect the user with the login information from platform
    redirectTo: '/',
  },
  {
    path: 'mobile-logout', // Custom page to allow the user to login again after logout without provoking an infinite relead with platform
    component: LogoutComponent,
  },
  {
    path: '**',
    redirectTo: nav => {
      if (nav.url[0]?.path === 'organizations') {
        return defaultRoute; // The specified route is already prefixed with the organization and was still not found. Default to the home page.
      }
      const router = inject(Router);
      const currentUserService = inject(CurrentUserService);
      const currentUser = currentUserService.currentUser();
      const paths = nav.url.map(s => s.path);
      // Without a valid user, we can't determine the correct organization to route to, so we send them to the user-redirect page.
      // After user is set, they'll be redirected to the URL they originally intended to visit.
      if (!currentUser) {
        return router.createUrlTree(['/user-redirect'], {
          queryParams: {
            returnUrl: router.createUrlTree(paths, { queryParams: nav.queryParams, fragment: nav.fragment ?? undefined }).toString(),
          },
        });
      }
      // The user accessed a non-prefixed org route. We need to prefix it with the correct organization and then navigate there.
      return router.createUrlTree(['/organizations', currentUser.organizationId].concat(paths), {
        queryParams: nav.queryParams,
        fragment: nav.fragment ?? undefined,
      });
    },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: CustomPreloadingStrategyService,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
